'use client';

import { FC, ReactNode, useCallback } from 'react';
import { throttle } from '@bts-web/utils-functions';
import { useClientTranslation } from '@bts-web/utils-lokalise';
import { css } from '@bts-web/utils-style-engine';
import { ButtonBase, ButtonVisualVariants } from '../ButtonBase/ButtonBase';
import { useAppNotification } from '../../../notifications/NotificationContextProvider';
import { RegularCopy } from '../../../common/svgIcons';

export const CopyToClipboardButton: FC<{
  value: string;
  className?: string;
  children?: string | ReactNode;
  ariaLabel: string;
  fullWidth?: boolean;
  noBorder?: boolean;
  visual?: ButtonVisualVariants;
  successMessage?: string;
  failedMessage?: string;
}> = ({
  value,
  className,
  children,
  ariaLabel,
  fullWidth,
  noBorder = false,
  visual = 'secondary',
  successMessage,
  failedMessage,
}) => {
  const { t } = useClientTranslation();

  const { setAppNotification } = useAppNotification();

  const copyToClipboardAndShowNotification = async () => {
    try {
      await navigator.clipboard.writeText(value);

      setAppNotification({
        title: successMessage || t('success_toast_message_title'),
        visual: 'success',
        customDuration: 3000,
      });
    } catch (_error) {
      setAppNotification({
        title: failedMessage || t('general_error_message_title'),
        visual: 'error',
        customDuration: 3000,
      });
    }
  };

  const handleCopyToClipboard = useCallback(
    throttle(() => copyToClipboardAndShowNotification(), 4000),
    [value],
  );

  return (
    <ButtonBase
      onClick={() => handleCopyToClipboard()}
      visual={visual}
      size="small"
      aria-label={ariaLabel}
      className={`${css({
        p: 0,
        minHeight: '28px',
        ...(!noBorder
          ? {
              borderColor: 'neutrals.fill_tertiary',
            }
          : {}),
        overflow: 'hidden',
      })} ${className ?? ''}`}
      fullWidth={fullWidth}
      data-testid="copy-clipboard-button"
    >
      {children}
      <RegularCopy />
    </ButtonBase>
  );
};
